import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaCoins, FaPlusCircle, FaWallet, FaArrowDown,
  FaCheckCircle, FaClock, FaTimesCircle, FaTimes, FaHistory, FaShieldAlt
} from "react-icons/fa";
import Navbar from "../components/Navbar";
import Breadcrumb from "../Views/BreadCrumb";

const BASE_URL = "https://api.dreamtoday.in";
const TXN_PER_PAGE = 5;
const WD_PER_PAGE = 5;
const MIN_WITHDRAWAL = 500;

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

/* ══════════════════════════════════════
   SUCCESS MODAL
══════════════════════════════════════ */
const SuccessModal = ({ amount, onClose }) => {
  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-8 w-full max-w-sm shadow-2xl border border-green-100 text-center"
        initial={{ scale: 0.7, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.7, y: 40 }}
        transition={{ type: "spring", stiffness: 300, damping: 22 }}
      >
        <motion.div
          className="w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-emerald-500
            flex items-center justify-center mx-auto mb-5 shadow-lg"
          initial={{ scale: 0 }} animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 260, damping: 18 }}
        >
          <FaCheckCircle className="text-white text-4xl" />
        </motion.div>

        <h3 className="text-2xl font-bold text-gray-800 mb-2">Request Submitted!</h3>
        <p className="text-gray-500 text-sm mb-4">
          Your withdrawal request for{" "}
          <span className="font-bold text-amber-600">{amount} coins</span> has been received.
          <br />We'll process it within 24–48 hours.
        </p>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 mb-6 flex items-center justify-center gap-2">
          <FaClock className="text-amber-500 text-sm" />
          <span className="text-amber-700 text-sm font-medium">Processing time: 24–48 hours</span>
        </div>

        <button
          onClick={onClose}
          className="w-full py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600
            text-white font-semibold text-sm shadow hover:scale-[1.02] transition"
        >
          Done
        </button>
      </motion.div>
    </motion.div>,
    document.body
  );
};

/* ══════════════════════════════════════
   WITHDRAWAL MODAL
══════════════════════════════════════ */
const WithdrawModal = ({ walletBalance, onClose, onSuccess }) => {
  const [amount, setAmount] = useState("");
  const [upiId, setUpiId] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [platformFeeAmount, setPlatformFeeAmount] = useState(0);
  const [feeLoading, setFeeLoading] = useState(true);

  useEffect(() => {
    const fetchPlatformCharge = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/admin/platform-charge`);
        const data = await res.json();
        if (data.success) {
          setPlatformFeeAmount(data.charge.amount);
        }
      } catch (err) {
        console.error("Failed to fetch platform charge:", err);
      } finally {
        setFeeLoading(false);
      }
    };
    fetchPlatformCharge();
  }, []);

  const numAmount = Number(amount) || 0;
  const platformFee = numAmount > 0 ? (numAmount * platformFeeAmount) / 100 : 0;
  const totalReceivable = numAmount > 0 ? numAmount - platformFee : 0;

  const validate = () => {
    const errs = {};
    const amt = Number(amount);
    if (!amount) errs.amount = "Amount is required.";
    else if (isNaN(amt) || amt <= 0) errs.amount = "Enter a valid amount.";
    else if (amt < MIN_WITHDRAWAL) errs.amount = `Minimum withdrawal is ${MIN_WITHDRAWAL} coins.`;
    else if (amt > walletBalance) errs.amount = `Insufficient balance. Max: ${walletBalance}`;
    if (!upiId.trim()) errs.upiId = "UPI ID is required.";
    else if (!/^[\w.\-_]{2,256}@[a-zA-Z]{2,64}$/.test(upiId.trim()))
      errs.upiId = "Enter a valid UPI ID (e.g. name@upi).";
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    const userId = sessionStorage.getItem("userId");
    setLoading(true); setApiError("");
    try {
      const res = await fetch(`${BASE_URL}/api/users/sendwalletredemptionreq/${userId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // ✅ FIX: send platform-fee-deducted amount instead of raw amount
        body: JSON.stringify({ amount: totalReceivable, upiId: upiId.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        onSuccess(totalReceivable);
        onClose();
      } else {
        setApiError(data.message || "Request failed. Please try again.");
      }
    } catch { setApiError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const inputCls = (field) =>
    `w-full px-4 py-3 rounded-xl border text-black text-sm outline-none transition
    ${errors[field]
      ? "border-red-300 bg-red-50 focus:border-red-400"
      : "border-yellow-200 bg-white focus:border-yellow-400 focus:ring-2 focus:ring-yellow-100"
    }`;

  const quickAmounts = [500, 1000, 2000, 5000];

  return createPortal(
    <motion.div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="bg-white rounded-3xl p-7 w-full max-w-md shadow-2xl border border-yellow-100"
        initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 30 }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-yellow-400 to-amber-500
              flex items-center justify-center shadow-md">
              <FaArrowDown className="text-white text-base" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">Withdraw Coins</h3>
              <p className="text-xs text-gray-400">Balance: <span className="font-semibold text-yellow-600">{walletBalance} coins</span></p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition">
            <FaTimes className="text-gray-500 text-sm" />
          </button>
        </div>

        {/* Minimum notice */}
        <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 mb-5">
          <FaCoins className="text-amber-500 text-sm shrink-0" />
          <p className="text-xs text-amber-700 font-medium">
            Minimum withdrawal amount is <span className="font-bold">{MIN_WITHDRAWAL} coins</span>
          </p>
        </div>

        {/* Quick amounts */}
        <p className="text-xs text-gray-500 font-medium mb-2">Quick Select</p>
        <div className="flex gap-2 mb-5 flex-wrap">
          {quickAmounts.map((q) => (
            <button
              key={q}
              onClick={() => { setAmount(String(q)); setErrors({ ...errors, amount: "" }); }}
              disabled={q > walletBalance}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold border transition
                ${amount === String(q)
                  ? "bg-gradient-to-r from-yellow-400 to-amber-500 text-white border-transparent shadow"
                  : "border-yellow-200 text-yellow-700 hover:bg-yellow-50"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              {q}
            </button>
          ))}
        </div>

        {/* Amount */}
        <div className="mb-4">
          <label className="text-xs text-gray-500 font-medium mb-1 block">Amount (coins) *</label>
          <input
            type="number"
            placeholder={`Min. ${MIN_WITHDRAWAL} coins`}
            value={amount}
            min={MIN_WITHDRAWAL}
            max={walletBalance}
            onChange={(e) => { setAmount(e.target.value); setErrors({ ...errors, amount: "" }); }}
            className={inputCls("amount")}
          />
          {errors.amount
            ? <p className="text-red-500 text-xs mt-1">{errors.amount}</p>
            : <p className="text-gray-400 text-xs mt-1">Enter at least {MIN_WITHDRAWAL} coins</p>
          }
        </div>

        {/* UPI */}
        <div className="mb-5">
          <label className="text-xs text-gray-500 font-medium mb-1 block">UPI ID *</label>
          <input
            placeholder="yourname@upi"
            value={upiId}
            onChange={(e) => { setUpiId(e.target.value); setErrors({ ...errors, upiId: "" }); }}
            className={inputCls("upiId")}
          />
          {errors.upiId && <p className="text-red-500 text-xs mt-1">{errors.upiId}</p>}
        </div>

        {/* ── PLATFORM FEE BREAKDOWN ── */}
        {numAmount >= MIN_WITHDRAWAL && (
          <div className="mb-5 bg-amber-50 border border-yellow-200 rounded-2xl px-5 py-4 space-y-2">
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-2">
              Payout Breakdown
            </h4>

            <div className="flex justify-between text-sm text-gray-600">
              <span>Withdrawal Amount</span>
              <span className="font-semibold text-gray-800">{numAmount} coins</span>
            </div>

            <div className="flex justify-between text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <FaShieldAlt className="text-yellow-400 text-xs" />
                Platform Fee ({platformFeeAmount}%)
              </span>
              {feeLoading ? (
                <span className="w-16 h-4 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="font-semibold text-red-500">− {platformFee.toFixed(2)} coins</span>
              )}
            </div>

            <div className="border-t border-dashed border-yellow-200 pt-2 flex justify-between items-center">
              <span className="font-bold text-gray-800 text-sm">You Receive</span>
              {feeLoading ? (
                <span className="w-20 h-5 bg-gray-200 rounded animate-pulse" />
              ) : (
                <span className="text-base font-bold text-amber-600">{totalReceivable.toFixed(2)} coins</span>
              )}
            </div>
          </div>
        )}

        {apiError && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
            ⚠️ {apiError}
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-3 rounded-xl border border-yellow-200 text-yellow-700 font-semibold hover:bg-yellow-50 transition text-sm">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={loading || feeLoading}
            className="flex-1 py-3 rounded-xl text-white font-semibold text-sm
              bg-gradient-to-r from-yellow-500 to-amber-600 shadow hover:scale-[1.02]
              transition disabled:opacity-60 flex items-center justify-center gap-2">
            {loading
              ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Processing...</>
              : "Submit Request"
            }
          </button>
        </div>

        {/* Secured note */}
        <p className="text-center text-xs text-gray-400 pt-4 flex items-center justify-center gap-1">
          <FaShieldAlt className="text-green-400" />
          Platform fee is deducted from withdrawal amount
        </p>
      </motion.div>
    </motion.div>,
    document.body
  );
};

/* ══════════════════════════════════════
   STATUS BADGE
══════════════════════════════════════ */
const StatusBadge = ({ status }) => {
  const map = {
    completed: { icon: FaCheckCircle, cls: "bg-green-50 text-green-600 border-green-200", label: "Completed" },
    pending: { icon: FaClock, cls: "bg-yellow-50 text-yellow-600 border-yellow-200", label: "Pending" },
    rejected: { icon: FaTimesCircle, cls: "bg-red-50 text-red-500 border-red-200", label: "Rejected" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${cfg.cls}`}>
      <Icon className="text-xs" /> {cfg.label}
    </span>
  );
};

/* ══════════════════════════════════════
   MAIN WALLET PAGE
══════════════════════════════════════ */
const Wallet = () => {
  const navigate = useNavigate();

  const [walletBalance, setWalletBalance] = useState(null);
  const [walletLoading, setWalletLoading] = useState(true);
  const [walletError, setWalletError] = useState("");

  const [transactions, setTransactions] = useState([]);
  const [txnLoading, setTxnLoading] = useState(true);
  const [txnError, setTxnError] = useState("");
  const [txnPage, setTxnPage] = useState(1);

  const [withdrawals, setWithdrawals] = useState([]);
  const [wdLoading, setWdLoading] = useState(true);
  const [wdError, setWdError] = useState("");
  const [wdPage, setWdPage] = useState(1);

  const [showWithdraw, setShowWithdraw] = useState(false);
  const [successAmount, setSuccessAmount] = useState(null);
  const [activeSection, setActiveSection] = useState("transactions");

  const txnTotal = Math.ceil(transactions.length / TXN_PER_PAGE);
  const txnPaged = [...transactions]
    .reverse()
    .slice((txnPage - 1) * TXN_PER_PAGE, txnPage * TXN_PER_PAGE);
  const wdTotal = Math.ceil(withdrawals.length / WD_PER_PAGE);
  const wdPaged = withdrawals.slice((wdPage - 1) * WD_PER_PAGE, wdPage * WD_PER_PAGE);

  const fetchWallet = async (userId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/users/mywallet/${userId}`);
      const data = await res.json();
      if (data.success) setWalletBalance(data.walletBalance);
      else setWalletError(data.message || "Failed to fetch balance.");
    } catch { setWalletError("Network error."); }
    finally { setWalletLoading(false); }
  };

  const fetchTransactions = async (userId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/users/transactionshistory/${userId}`);
      const data = await res.json();
      if (data.success) setTransactions(data.transactions);
      else setTxnError(data.message || "Failed to fetch transactions.");
    } catch { setTxnError("Network error."); }
    finally { setTxnLoading(false); }
  };

  const fetchWithdrawals = async (userId) => {
    setWdLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/users/getallredemptionreq/${userId}`);
      const data = await res.json();
      if (data.success) setWithdrawals(data.requests || []);
      else setWdError(data.message || "Failed to fetch withdrawals.");
    } catch { setWdError("Network error."); }
    finally { setWdLoading(false); }
  };

  useEffect(() => {
    const userId = sessionStorage.getItem("userId");
    if (!userId) {
      setWalletError("User not logged in.");
      setWalletLoading(false);
      setTxnLoading(false);
      setWdLoading(false);
      return;
    }
    fetchWallet(userId);
    fetchTransactions(userId);
    fetchWithdrawals(userId);
  }, []);

  const handleWithdrawSuccess = (amount) => {
    const userId = sessionStorage.getItem("userId");
    fetchWallet(userId);
    fetchWithdrawals(userId);
    setActiveSection("withdrawals");
    setSuccessAmount(amount);
  };

  const Pages = ({ current, total, onChange }) => {
    if (total <= 1) return null;

    const getPages = () => {
      if (total <= 7) return [...Array(total)].map((_, i) => i + 1);
      const pages = [];
      pages.push(1);
      if (current > 4) pages.push("...");
      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (current < total - 3) pages.push("...");
      pages.push(total);
      return pages;
    };

    const btnBase = "min-w-[36px] h-9 flex items-center justify-center rounded-lg text-sm font-medium transition select-none";

    return (
      <div className="flex justify-center mt-5 gap-1.5 items-center flex-wrap">
        <button
          disabled={current === 1}
          onClick={() => onChange(current - 1)}
          className={`${btnBase} px-3 bg-amber-100 hover:bg-amber-200 text-amber-700 disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          ‹ Prev
        </button>

        {getPages().map((p, idx) =>
          p === "..." ? (
            <span key={`ellipsis-${idx}`} className={`${btnBase} text-amber-400 cursor-default px-1`}>
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`${btnBase} px-0 ${current === p
                ? "bg-amber-600 text-white shadow-md scale-105"
                : "bg-amber-100 hover:bg-amber-200 text-amber-700"
                }`}
            >
              {p}
            </button>
          )
        )}

        <button
          disabled={current === total}
          onClick={() => onChange(current + 1)}
          className={`${btnBase} px-3 bg-amber-100 hover:bg-amber-200 text-amber-700 disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          Next ›
        </button>
      </div>
    );
  };

  const canWithdraw = walletBalance && walletBalance >= MIN_WITHDRAWAL;

  return (
    <>
      <Navbar />

      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-amber-100 pt-28 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">

          <div className="mb-6">
            <Breadcrumb items={[{ label: "My Account", path: "/myaccount" }, { label: "Wallet" }]} />
          </div>

          {/* ── WALLET CARD ── */}
          <div className="bg-gradient-to-r from-yellow-100 to-amber-200 rounded-3xl shadow-xl p-6 sm:p-8 mb-8 border border-yellow-300">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-semibold text-amber-800 mb-2">My Wallet</h2>
                <div className="flex items-center gap-2 text-3xl sm:text-4xl font-bold text-amber-900">
                  {walletLoading ? (
                    <span className="w-24 h-9 bg-amber-300/50 rounded-lg animate-pulse inline-block" />
                  ) : walletError ? (
                    <span className="text-red-500 text-xl">{walletError}</span>
                  ) : (
                    <>{walletBalance}<FaCoins className="text-yellow-500 drop-shadow-[0_0_6px_rgba(255,215,0,0.9)]" /></>
                  )}
                </div>
                <p className="text-amber-700 mt-2 text-sm sm:text-base">Available Balance</p>
                {!walletLoading && !walletError && (
                  <p className="text-amber-600 text-xs mt-1">
                    Min. withdrawal: <span className="font-semibold">{MIN_WITHDRAWAL} coins</span>
                    {walletBalance < MIN_WITHDRAWAL && walletBalance > 0 && (
                      <span className="ml-2 text-red-500 font-medium">
                        (Need {MIN_WITHDRAWAL - walletBalance} more)
                      </span>
                    )}
                  </p>
                )}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => setShowWithdraw(true)}
                  disabled={!canWithdraw}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl
                    bg-white/80 border border-yellow-300 text-amber-700 font-semibold
                    shadow hover:shadow-md hover:scale-105 transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <FaArrowDown /> Withdraw
                </button>
                <button
                  onClick={() => navigate("/packages")}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl
                    bg-gradient-to-r from-yellow-500 to-amber-600
                    text-white font-semibold shadow-lg hover:shadow-xl hover:scale-105 transition"
                >
                  <FaPlusCircle /> Add Coins
                </button>
              </div>
            </div>
          </div>

          {/* ── SECTION TABS ── */}
          <div className="flex gap-2 mb-6">
            {[
              { id: "transactions", label: "Transactions", icon: FaCoins },
              { id: "withdrawals", label: "Withdrawals", icon: FaHistory },
            ].map(({ id, label, icon: Icon }) => (
              <button key={id} onClick={() => setActiveSection(id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-semibold text-sm transition
                  ${activeSection === id
                    ? "bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow"
                    : "bg-white/70 border border-yellow-200 text-amber-700 hover:bg-yellow-50"
                  }`}>
                <Icon /> {label}
              </button>
            ))}
          </div>

          {/* ── TRANSACTIONS ── */}
          <AnimatePresence mode="wait">
            {activeSection === "transactions" && (
              <motion.div key="txn"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                className="bg-white/70 backdrop-blur-md rounded-3xl shadow-lg p-4 sm:p-6 border border-yellow-200"
              >
                <h3 className="text-lg sm:text-xl font-semibold text-amber-800 mb-4">Transaction History</h3>

                {txnLoading && <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" /></div>}
                {!txnLoading && txnError && <p className="text-center py-8 text-red-500">{txnError}</p>}
                {!txnLoading && !txnError && transactions.length === 0 && <p className="text-center py-8 text-amber-600">No transactions found.</p>}

                {!txnLoading && !txnError && transactions.length > 0 && (
                  <>
                    <div className="space-y-3">
                      {txnPaged.map((txn) => {
                        const isCredit = txn.coinsAdded > 0;
                        const amount = isCredit ? txn.coinsAdded : txn.coinsDeducted;
                        return (
                          <div key={txn._id}
                            className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3
                              bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-xl shadow-sm border border-yellow-200">
                            <div>
                              <p className="font-medium text-amber-900">
                                {isCredit ? "Coins Added" : "Coins Deducted"}
                              </p>
                              <p className="text-sm text-amber-600">{formatDate(txn.createdAt)}</p>
                              {txn.description?.trim() && (
                                <p className="text-xs text-gray-500 mt-1 line-clamp-2">{txn.description}</p>
                              )}
                            </div>
                            <span className={`font-bold text-lg ${isCredit ? "text-green-600" : "text-red-500"}`}>
                              {isCredit ? "+" : "-"}{amount} Coins
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <Pages current={txnPage} total={txnTotal} onChange={setTxnPage} />
                  </>
                )}
              </motion.div>
            )}

            {/* ── WITHDRAWALS ── */}
            {activeSection === "withdrawals" && (
              <motion.div key="wd"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }}
                className="bg-white/70 backdrop-blur-md rounded-3xl shadow-lg p-4 sm:p-6 border border-yellow-200"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg sm:text-xl font-semibold text-amber-800">Withdrawal Requests</h3>
                  <button
                    onClick={() => setShowWithdraw(true)}
                    disabled={!canWithdraw}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold
                      bg-gradient-to-r from-yellow-500 to-amber-600 text-white shadow
                      hover:scale-105 transition disabled:opacity-40 disabled:cursor-not-allowed">
                    <FaArrowDown /> New Request
                  </button>
                </div>

                {wdLoading && <div className="flex justify-center py-10"><div className="w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full animate-spin" /></div>}
                {!wdLoading && wdError && <p className="text-center py-8 text-red-500">{wdError}</p>}
                {!wdLoading && !wdError && withdrawals.length === 0 && (
                  <div className="text-center py-10">
                    <FaWallet className="text-4xl text-amber-200 mx-auto mb-3" />
                    <p className="text-amber-600 font-medium">No withdrawal requests yet.</p>
                    <p className="text-amber-500 text-sm mt-1">Submit a request to withdraw your coins.</p>
                  </div>
                )}

                {!wdLoading && !wdError && withdrawals.length > 0 && (
                  <>
                    <div className="space-y-3">
                      {wdPaged.map((req) => (
                        <motion.div key={req._id}
                          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                          className="flex flex-col sm:flex-row sm:items-center justify-between gap-3
                            bg-gradient-to-r from-yellow-50 to-amber-50 p-4 rounded-xl border border-yellow-200 shadow-sm">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500
                              flex items-center justify-center shadow-sm shrink-0">
                              <FaArrowDown className="text-white text-sm" />
                            </div>
                            <div>
                              <p className="font-bold text-amber-900 text-base">{req.amount} Coins</p>
                              <p className="text-xs text-gray-400">{formatDate(req.createdAt)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {req.updatedAt !== req.createdAt && (
                              <span className="text-xs text-gray-400">Updated {formatDate(req.updatedAt)}</span>
                            )}
                            <StatusBadge status={req.status} />
                          </div>
                        </motion.div>
                      ))}
                    </div>
                    <Pages current={wdPage} total={wdTotal} onChange={setWdPage} />
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>

        </div>
      </div>

      {/* WITHDRAW MODAL */}
      <AnimatePresence>
        {showWithdraw && (
          <WithdrawModal
            walletBalance={walletBalance || 0}
            onClose={() => setShowWithdraw(false)}
            onSuccess={handleWithdrawSuccess}
          />
        )}
      </AnimatePresence>

      {/* SUCCESS MODAL */}
      <AnimatePresence>
        {successAmount !== null && (
          <SuccessModal
            amount={successAmount}
            onClose={() => setSuccessAmount(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default Wallet;